import { Switch } from "@headlessui/react";
import { Icon } from "@iconify/react";
import React, { useEffect, useState } from "react";
import ReactModal from "react-modal";
import { toast } from "react-toastify";
import UpdateHooks from "../../../Hooks/UpdateHooks";

const ResellerEditProfileModal = ({ resellerData, isOpen, closeModal }) => {
  const [account, setAccount] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const generatePassword = (event) => {
    event.preventDefault();
    const chars =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let generatedPassword = "";
    for (let i = 0; i < 8; i++) {
      generatedPassword += chars[Math.floor(Math.random() * chars.length)];
    }
    setPassword(generatedPassword);
  };

  useEffect(() => {
    setAccount(resellerData?.isActive);
  }, [isOpen]);

  const handelUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    const name = e.target?.name.value;
    const email = e.target?.email.value;
    const dateOfBirth = e.target?.dateOfBirth.value;
    const phone = e.target?.phone.value;

    const payload = { name, phone, dateOfBirth, email };
    if (password) payload.password = password;

    try {
      await UpdateHooks(
        `https://servercareseebd.careseebd.com/api/v1/user/${resellerData?._id}`,
        payload
      );
      toast.success("Profile updated successfully!");
      closeModal();
    } catch (error) {
      toast.error("Failed to update profile.");
    } finally {
      setLoading(false);
    }
  };

  const handelUpdateAccount = async () => {
    const next = !account;
    setAccount(next);
    await UpdateHooks(
      `https://servercareseebd.careseebd.com/api/v1/user/${resellerData?._id}`,
      { isActive: next }
    );
    toast.success(`Account ${next ? "activated" : "deactivated"}!`);
  };

  const inputClass =
    "w-full py-2.5 pl-10 pr-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-violet-500 focus:bg-white focus:ring-4 focus:ring-violet-500/10 transition-all duration-200";

  const iconClass =
    "absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4";

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={closeModal}
      className="fixed z-50 inset-0 flex items-center justify-center p-4"
      overlayClassName="fixed inset-0 z-40"
      style={{
        overlay: {
          backgroundColor: "rgba(15, 23, 42, 0.6)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
        },
        content: {
          border: "none",
          background: "transparent",
          padding: 0,
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header gradient bar */}
        <div className="h-1.5 w-full bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500" />

        <div className="px-8 pt-7 pb-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900 tracking-tight">
                Edit Reseller
              </h2>
              <p className="text-sm text-gray-400 mt-0.5">
                {resellerData?.name || "Update reseller profile"}
              </p>
            </div>
            <button
              type="button"
              onClick={closeModal}
              className="flex items-center justify-center w-9 h-9 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Account toggle */}
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl mb-5">
            <div>
              <p className="text-sm font-semibold text-gray-700">
                Account Status
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                {account ? "Active — can log in" : "Inactive — access blocked"}
              </p>
            </div>
            <Switch
              checked={account}
              onClick={handelUpdateAccount}
              onChange={setAccount}
              className={`${account ? "bg-violet-600" : "bg-gray-300"
                } relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2`}
            >
              <span className="sr-only">Account status</span>
              <span
                aria-hidden="true"
                className={`${account ? "translate-x-5" : "translate-x-0"
                  } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out`}
              />
            </Switch>
          </div>

          {/* Form */}
          <form onSubmit={handelUpdate} className="space-y-3.5">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                Full Name
              </label>
              <div className="relative">
                <Icon icon="mdi:user" className={iconClass} />
                <input
                  type="text"
                  name="name"
                  className={inputClass}
                  defaultValue={resellerData?.name}
                  placeholder="Full name"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                Email Address
              </label>
              <div className="relative">
                <Icon icon="ic:baseline-email" className={iconClass} />
                <input
                  type="email"
                  name="email"
                  className={inputClass}
                  defaultValue={resellerData?.email}
                  placeholder="Email address"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                Date of Birth
              </label>
              <div className="relative">
                <Icon icon="ph:calendar-fill" className={iconClass} />
                <input
                  type="date"
                  name="dateOfBirth"
                  className={inputClass}
                  defaultValue={resellerData?.dateOfBirth}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                Phone Number
              </label>
              <div className="relative">
                <Icon icon="fluent:call-20-filled" className={iconClass} />
                <input
                  type="tel"
                  name="phone"
                  className={inputClass}
                  defaultValue={resellerData?.phone}
                  placeholder="Phone number"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                Password
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={password}
                  name="password"
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Leave blank to keep current"
                  className="flex-1 py-2.5 px-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-violet-500 focus:bg-white focus:ring-4 focus:ring-violet-500/10 transition-all duration-200 font-mono"
                />
                <button
                  onClick={generatePassword}
                  className="shrink-0 px-3.5 py-2.5 bg-gray-100 hover:bg-violet-50 border border-gray-200 hover:border-violet-300 text-gray-600 hover:text-violet-700 text-xs font-semibold rounded-xl transition-all duration-200"
                >
                  Generate
                </button>
              </div>
            </div>

            <div className="pt-3">
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-6 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white text-sm font-semibold rounded-xl shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg
                      className="animate-spin h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Saving...
                  </span>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </ReactModal>
  );
};

export default ResellerEditProfileModal;
